import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "@/lib/apiClient";
import { scoreColor } from "@/lib/status";
import { TrendUp, Gauge, ShieldWarning, CurrencyDollar, ChartLineUp, Trophy } from "@phosphor-icons/react";
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, BarChart, Bar,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from "recharts";

const RISK_COLORS = { LOW: "#059669", MEDIUM: "#D97706", HIGH: "#EA580C", CRITICAL: "#DC2626" };

function Stat({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={`analytics-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="font-mono-plex text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">{label}</span>
        <Icon size={18} color={accent} weight="bold" />
      </div>
      <div className="font-display font-black text-3xl tracking-tighter mt-3 text-[#0F172A]">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

const Panel = ({ title, subtitle, children, testId }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid={testId}>
    <h2 className="font-display font-bold text-lg text-[#0F172A]">{title}</h2>
    {subtitle && <p className="text-xs text-slate-400 mt-0.5 mb-4">{subtitle}</p>}
    <div className={subtitle ? "" : "mt-4"}>{children}</div>
  </div>
);

const tooltipStyle = { borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 };

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("/analytics/overview").then((r) => setD(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-slate-400 animate-pulse">Loading analytics…</div>;

  if (!d || d.count === 0) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-[#0F172A]">Analytics</h1>
          <p className="text-slate-500 mt-2">Margin, labor and score trends across all your proposals.</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-16 text-center" data-testid="analytics-empty">
          <ChartLineUp size={40} className="mx-auto text-slate-300" weight="duotone" />
          <div className="font-display font-bold text-lg text-[#0F172A] mt-4">No analyzed proposals yet</div>
          <p className="text-slate-500 mt-1 text-sm">Analyze a few proposals and your trends will show up here.</p>
          <button data-testid="analytics-new-cta" onClick={() => navigate("/new")} className="mt-6 inline-flex items-center gap-2 h-11 px-5 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md font-semibold text-sm transition-colors">Analyze a Proposal</button>
        </div>
      </div>
    );
  }

  const trend = d.trend.map((t, i) => ({ ...t, n: i + 1, label: `#${i + 1}` }));

  return (
    <div className="space-y-8" data-testid="analytics-page">
      <div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-[#0F172A]">Analytics</h1>
        <p className="text-slate-500 mt-2">Margin, labor and score trends across all {d.count} analyzed proposals.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <Stat label="Analyzed" value={d.count} icon={TrendUp} accent="#0F172A" />
        <Stat label="Win Rate" value={d.win_rate != null ? `${d.win_rate}%` : "—"} sub={`${d.outcome_counts?.won || 0}W · ${d.outcome_counts?.lost || 0}L`} icon={Trophy} accent="#059669" />
        <Stat label="Avg Forensics" value={d.avg_iq} sub="out of 100" icon={Gauge} accent={scoreColor(d.avg_iq)} />
        <Stat label="Avg Risk" value={d.avg_risk} sub="higher = safer" icon={ShieldWarning} accent={scoreColor(d.avg_risk)} />
        <Stat label="Avg Margin" value={d.avg_margin != null ? `${d.avg_margin}%` : "—"} sub="gross" icon={ChartLineUp} accent="#2563EB" />
        <Stat label="Pipeline" value={`$${(d.total_value / 1000).toFixed(0)}k`} sub="total value" icon={CurrencyDollar} accent="#059669" />
      </div>

      <Panel title="Deal Outcomes" subtitle="Win rate and value across decided proposals" testId="chart-outcomes">
        <div className="grid sm:grid-cols-3 gap-8 items-center">
          <div className="text-center" data-testid="win-rate-big">
            <div className="font-display font-black text-6xl tracking-tighter text-[#0F172A]">{d.win_rate != null ? `${d.win_rate}%` : "—"}</div>
            <div className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-2">Win rate</div>
          </div>
          <div className="sm:col-span-2 space-y-3">
            {[["won", "Won", "#059669"], ["lost", "Lost", "#DC2626"], ["pending", "Pending", "#94A3B8"]].map(([k, label, color]) => {
              const c = d.outcome_counts?.[k] || 0;
              const pct = d.count ? (c / d.count) * 100 : 0;
              return (
                <div key={k} data-testid={`outcome-bar-${k}`}>
                  <div className="flex justify-between text-sm mb-1"><span className="text-slate-600">{label}</span><span className="font-display font-bold text-[#0F172A]">{c}</span></div>
                  <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: color }} /></div>
                </div>
              );
            })}
            <div className="flex flex-wrap gap-x-8 gap-y-2 pt-3 border-t border-slate-100 text-sm">
              <div><span className="text-slate-400">Won value </span><span className="font-display font-bold text-emerald-700">${(d.won_value / 1000).toFixed(0)}k</span></div>
              <div><span className="text-slate-400">Lost value </span><span className="font-display font-bold text-red-600">${(d.lost_value / 1000).toFixed(0)}k</span></div>
            </div>
          </div>
        </div>
      </Panel>

      <Panel title="Score Trend" subtitle="Proposal Forensics score & risk score over time (oldest → newest)" testId="chart-score-trend">
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94A3B8" }} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#94A3B8" }} />
            <Tooltip contentStyle={tooltipStyle} labelFormatter={(l, pl) => pl?.[0]?.payload?.project || l} />
            <Line type="monotone" dataKey="iq" name="Forensics Score" stroke="#2563EB" strokeWidth={2.5} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="risk" name="Risk Score" stroke="#0F172A" strokeWidth={2.5} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </Panel>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Gross Margin Trend" subtitle="Estimated gross margin % per proposal" testId="chart-margin-trend">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="marginFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563EB" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94A3B8" }} />
              <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} unit="%" />
              <Tooltip contentStyle={tooltipStyle} labelFormatter={(l, pl) => pl?.[0]?.payload?.project || l} />
              <Area type="monotone" dataKey="margin" name="Margin %" stroke="#2563EB" strokeWidth={2.5} fill="url(#marginFill)" connectNulls />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Risk Distribution" subtitle="How your proposals score on risk" testId="chart-risk-dist">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={d.risk_distribution} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="level" tick={{ fontSize: 11, fill: "#94A3B8" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94A3B8" }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" name="Proposals" radius={[6, 6, 0, 0]}>
                {d.risk_distribution.map((r) => <Cell key={r.level} fill={RISK_COLORS[r.level] || "#94A3B8"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Category Strengths" subtitle="Average subscore across the 8 review dimensions" testId="chart-subscores">
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={d.subscore_avg} outerRadius="72%">
              <PolarGrid stroke="#E2E8F0" />
              <PolarAngleAxis dataKey="category" tick={{ fontSize: 10, fill: "#64748B" }} />
              <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 9, fill: "#CBD5E1" }} />
              <Radar name="Avg Score" dataKey="score" stroke="#2563EB" fill="#2563EB" fillOpacity={0.35} strokeWidth={2} />
              <Tooltip contentStyle={tooltipStyle} />
            </RadarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Recurring Risks" subtitle="Most common risk categories flagged across proposals" testId="chart-top-risks">
          {d.top_risks.length === 0 ? (
            <div className="text-sm text-slate-400 py-10 text-center">No recurring risks flagged yet.</div>
          ) : (
            <div className="space-y-3 mt-1">
              {d.top_risks.map((r) => {
                const max = d.top_risks[0].count || 1;
                return (
                  <div key={r.category} data-testid={`top-risk-${r.category}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-600">{r.category}</span>
                      <span className="font-display font-bold text-sm text-[#0F172A]">{r.count}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full bg-[#DC2626]/80 transition-all duration-700" style={{ width: `${(r.count / max) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
